<div class="space-y-6" [formGroup]="form()">
  <section class="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
    <div>
      <h3 class="text-sm font-bold text-slate-900">Land Adjustments</h3>
      <p class="mt-1 text-sm text-slate-500">Orientation, road width and location premium refine the land value.</p>
    </div>
    <div class="grid gap-4 sm:grid-cols-3">
      <label class="space-y-1.5 text-sm font-semibold text-slate-700">
        Orientation <span class="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">MANUAL INPUT</span>
        <select class="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-normal outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30" (change)="orientation.setValue($any($event.target).value); orientation.markAsDirty()">
          @for (option of orientationOptions; track option.value) { <option [selected]="option.value === orientation.value" [value]="option.value">{{ option.label }}</option> }
        </select>
      </label>
      <label class="space-y-1.5 text-sm font-semibold text-slate-700">
        Road Width <span class="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">MANUAL INPUT</span>
        <select class="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-normal outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30" (change)="roadAdjustment.setValue(+$any($event.target).value); roadAdjustment.markAsDirty()">
          @for (option of roadOptions; track option.value) { <option [selected]="option.value === +roadAdjustment.value" [value]="option.value">{{ option.label }}</option> }
        </select>
      </label>
      <label class="space-y-1.5 text-sm font-semibold text-slate-700">
        Main Road / Premium Plot <span class="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">MANUAL INPUT</span>
        <select class="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-normal outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30" (change)="premiumAdjustment.setValue(+$any($event.target).value); premiumAdjustment.markAsDirty()">
          @for (option of premiumOptions; track option.value) { <option [selected]="option.value === +premiumAdjustment.value" [value]="option.value">{{ option.label }}</option> }
        </select>
      </label>
    </div>

    <div class="grid gap-4 sm:grid-cols-2">
      <ui-calculated-box label="Total Land Adjustment" [value]="pct(land()?.totalLandAdjustment)" derivation="Orientation + Road Width + Premium Plot" />
      <ui-calculated-box label="Adjusted Land Value" [value]="compact(land()?.adjustedLandValue)" derivation="Land Value after adjustments" />
    </div>

    <div class="space-y-4 rounded-lg border border-slate-200 px-4 py-3">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <label class="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-slate-700">
          <input type="checkbox" formControlName="supplyOverride" class="size-4 rounded border-slate-300 accent-indigo-600" />
          Supply Override
          <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">INTERNAL</span>
        </label>
        @if (supplyOverride.value) {
          <button type="button" (click)="clearOverride()" class="text-xs font-semibold text-indigo-600 hover:text-indigo-800">
            Clear override
          </button>
        }
      </div>

      @if (supplyOverride.value) {
        <p class="text-xs text-slate-500">
          Replaces the total assessed land value for exceptional properties. Enter the full land value in ₹, not a rate per sq ft.
        </p>
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="space-y-1">
            <ui-manual-input
              [control]="supplyOverrideValue"
              label="Override Land Value (₹)"
              placeholder="40000000"
            />
            @if (overrideInCrore(); as cr) {
              <p class="text-sm font-semibold text-indigo-700">= {{ cr }}</p>
            }
            @if (valueMissing()) {
              <p class="text-xs font-medium text-red-600" role="alert">Enter the override land value.</p>
            }
            <p class="text-xs text-slate-500">Calculated value: {{ compact(land()?.adjustedLandValue) }}</p>
          </div>
          <label class="space-y-1.5 text-sm font-semibold text-slate-900">
            Reason / Comment <span class="text-red-600">*</span>
            <textarea
              formControlName="supplyOverrideReason"
              rows="3"
              placeholder="Why this property needs an override"
              [attr.aria-invalid]="reasonMissing()"
              class="w-full rounded-lg border bg-white px-4 py-2.5 text-[15px] font-normal text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
              [class]="reasonMissing() ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-500'"
            ></textarea>
            @if (reasonMissing()) {
              <span class="block text-xs font-medium text-red-600" role="alert">A reason is required for a Supply Override.</span>
            }
          </label>
        </div>
      }
    </div>

    <!-- A plot's Final CI Value (Step 10) sits beside its Final Land Value. -->
    <div class="grid gap-4" [class.sm:grid-cols-2]="isPlot()">
      <ui-calculated-box
        label="Final Land Value"
        [value]="compact(land()?.finalLandValue)"
        [derivation]="land()?.supplyOverrideApplied ? 'Supply Override total (calculated ' + compact(land()?.adjustedLandValue) + ')' : 'Adjusted Land Value; no override'"
      />
      @if (isPlot()) {
        <ui-calculated-box
          label="Final CI Value"
          [value]="compact(land()?.assessedValue)"
          [derivation]="finalCiFormula()"
        />
      }
    </div>


    <!-- Steps 11–15 of the spec, for plots (houses show them on the Building Value step). -->
    @if (isPlot()) {
    <div class="space-y-4">
      <div class="grid gap-4 sm:grid-cols-2 mt-5">
        <ui-calculated-box
          label="Ask vs CI Value ratio"
          [value]="ratio(land()?.askVsCiPercent)"
          derivation="Step 11 · (Current Asking Price ÷ Final CI Value) − 1"
        />
        <ui-calculated-box
          label="Ask vs CI Value %"
          [value]="pct(land()?.askVsCiPercent, 2)"
          derivation="Step 12 · Ask vs CI Value ratio × 100"
        />
      </div>
      <div class="grid gap-4 sm:grid-cols-3">
        <ui-calculated-box
          label="Suggested Offer"
          [value]="compact(result()?.suggestedOfferValue)"
          derivation="Step 13 · Final CI Value × 0.95"
        />
        <ui-calculated-box
          label="Likely Close Low"
          [value]="compact(result()?.likelyCloseValueLow)"
          derivation="Step 14 · Final CI Value × 0.97"
        />
        <ui-calculated-box
          label="Likely Close High"
          [value]="compact(result()?.likelyCloseValueHigh)"
          derivation="Step 15 · Final CI Value × 1.03"
        />
      </div>
      <ui-calculated-box
        label="Customer Verdict"
        [value]="result()?.circledIntelligenceVerdict || '—'"
        [derivation]="result()?.verdictDescription || ''"
      />
    </div>
    }
  </section>

  @if (calculationError()) {
    <ui-note-bar tone="danger">{{ calculationError() }}</ui-note-bar>
  } @else if (calculating() && !land()) {
    <ui-note-bar tone="info">Calculating…</ui-note-bar>
  }
</div>
